import { useEffect, useState } from "react";
import api from "@/lib/api";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { DollarSign, TrendingUp, Users, Loader2, Filter, ArrowRight, Mail } from "lucide-react";

const fmt = (n) => "$" + Math.round(n).toLocaleString();

function Stat({ label, value, sub, icon: Icon, accent }) {
  return (
    <div className="bg-white border border-slate-200 rounded-md p-5 shadow-sm">
      <div className="flex items-center justify-between mb-2"><span className="overline">{label}</span><Icon className="h-4 w-4 text-slate-400" /></div>
      <div className={`font-display font-black text-3xl tracking-tight ${accent || ""}`}>{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}

const BAR_COLORS = ["#64748B", "#0284C7", "#0F172A"];
const CHART_MARGIN = { top: 5, right: 5, left: 5, bottom: 5 };
const X_TICK = { fontSize: 12, fill: "#64748B" };
const Y_TICK = { fontSize: 11, fill: "#94A3B8" };
const yTickFmt = (v) => `$${v / 1000}k`;

export default function Analytics() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/admin/analytics").then((r) => setD(r.data)).catch((e) => setErr(e.response?.status === 403 ? "Admin access required." : "Failed to load."));
  }, []);

  if (err) return <div className="p-10 text-slate-500" data-testid="analytics-error">{err}</div>;
  if (!d) return <div className="p-10 flex items-center gap-2 text-slate-500"><Loader2 className="h-5 w-5 animate-spin" /> Loading…</div>;

  const funnelSteps = [
    { label: "Free-score leads", value: d.funnel.score_leads },
    { label: "Signups", value: d.funnel.signups, conv: `${d.funnel.lead_to_signup_pct}% of leads` },
    { label: "Paying customers", value: d.funnel.paid, conv: `${d.funnel.signup_to_paid_pct}% of signups` },
  ];

  return (
    <div className="p-8 max-w-7xl" data-testid="analytics-page">
      <div className="mb-6">
        <div className="overline mb-1">Founder Analytics</div>
        <h1 className="font-display font-bold text-3xl tracking-tight">Growth Dashboard</h1>
        <p className="text-slate-500 text-sm mt-1">The $10M-ARR engine — MRR, funnel conversion, and pipeline value across all accounts.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <Stat label="MRR" value={fmt(d.mrr.total)} sub={`${d.mrr.paid_customers} paying customers`} icon={DollarSign} accent="text-success" />
        <Stat label="ARR (run-rate)" value={fmt(d.mrr.arr)} sub="MRR × 12" icon={TrendingUp} />
        <Stat label="Signups" value={d.funnel.signups} sub={`${d.funnel.signup_to_paid_pct}% convert to paid`} icon={Users} />
        <Stat label="Pipeline value" value={fmt(d.pipeline.total_value)} sub={`${d.pipeline.leads_total} outbound leads`} icon={Filter} />
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mb-6">
        <div className="bg-white border border-slate-200 rounded-md p-6 shadow-sm">
          <h2 className="font-display font-bold text-lg mb-4">MRR by tier</h2>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={d.mrr.by_tier} margin={CHART_MARGIN}>
              <XAxis dataKey="tier" tick={X_TICK} />
              <YAxis tickFormatter={yTickFmt} tick={Y_TICK} />
              <Tooltip formatter={(v, n, p) => [fmt(v), `${p.payload.customers} cust × $${p.payload.price}`]} />
              <Bar dataKey="mrr" radius={[4, 4, 0, 0]}>
                {d.mrr.by_tier.map((t) => <Cell key={t.key} fill={BAR_COLORS[d.mrr.by_tier.indexOf(t) % BAR_COLORS.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <div className="grid grid-cols-3 gap-2 mt-3">
            {d.mrr.by_tier.map((t) => (
              <div key={t.key} className="text-center" data-testid={`mrr-${t.key}`}>
                <div className="font-display font-bold">{t.customers}</div>
                <div className="text-xs text-slate-500">{t.tier}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-md p-6 shadow-sm">
          <h2 className="font-display font-bold text-lg mb-4">Acquisition funnel</h2>
          <div className="space-y-3">
            {funnelSteps.map((s, i) => {
              const max = funnelSteps[0].value || 1;
              const w = Math.max(8, Math.round(100 * s.value / max));
              return (
                <div key={s.label} data-testid={`funnel-step-${i}`}>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="font-medium text-slate-700">{s.label}</span>
                    <span className="font-display font-bold">{s.value}{s.conv && <span className="text-xs text-slate-400 font-normal ml-2">{s.conv}</span>}</span>
                  </div>
                  <div className="h-7 bg-slate-100 rounded">
                    <div className="h-7 rounded bg-brand-accent/90 flex items-center justify-end pr-2" style={{ width: `${w}%` }}>
                      {i < funnelSteps.length - 1 && <ArrowRight className="h-3 w-3 text-white/80" />}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {d.nurture && (
        <div className="bg-white border border-slate-200 rounded-md p-6 shadow-sm mb-6" data-testid="nurture-panel">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display font-bold text-lg flex items-center gap-2"><Mail className="h-4 w-4 text-brand-accent" /> Lead nurture sequence</h2>
            <span className={`text-xs font-semibold px-2 py-1 rounded ${d.nurture.enabled ? "bg-success-bg text-success" : "bg-warning-bg text-warning"}`} data-testid="nurture-status">
              {d.nurture.enabled ? "Active" : "Awaiting email key"}
            </span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
            <div data-testid="nurture-total"><div className="font-display font-black text-2xl">{d.nurture.total}</div><div className="text-xs text-slate-500">In sequence (all-time)</div></div>
            <div data-testid="nurture-active"><div className="font-display font-black text-2xl text-brand-accent">{d.nurture.active}</div><div className="text-xs text-slate-500">Currently nurturing</div></div>
            <div data-testid="nurture-converted"><div className="font-display font-black text-2xl text-success">{d.nurture.converted}</div><div className="text-xs text-slate-500">Converted · {d.nurture.converted_pct}%</div></div>
            <div data-testid="nurture-unsub"><div className="font-display font-black text-2xl text-slate-400">{d.nurture.unsubscribed}</div><div className="text-xs text-slate-500">Unsubscribed</div></div>
          </div>
          <div className="space-y-2">
            {d.nurture.by_stage.map((s) => {
              const max = d.nurture.total || 1;
              const w = Math.max(4, Math.round(100 * s.reached / max));
              const labels = ["Email 1 · 24h reminder", "Email 2 · 72h prime-screening", "Email 3 · 7-day final offer"];
              return (
                <div key={s.stage} data-testid={`nurture-stage-${s.stage}`}>
                  <div className="flex justify-between text-xs mb-1"><span className="text-slate-600">{labels[s.stage] || `Email ${s.stage + 1}`}</span><span className="font-mono text-slate-500">{s.reached} sent</span></div>
                  <div className="h-5 bg-slate-100 rounded"><div className="h-5 rounded bg-brand/80" style={{ width: `${w}%` }} /></div>
                </div>
              );
            })}
          </div>
          {!d.nurture.enabled && <p className="text-xs text-slate-400 mt-3">Add a Resend API key to start sending these follow-ups automatically.</p>}
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-md shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 font-display font-bold">Outbound lead pipeline</div>
        <table className="w-full text-sm">
          <thead><tr className="bg-slate-50 border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-500">
            <th className="px-6 py-3">Status</th><th className="px-3 py-3 text-right">Leads</th><th className="px-6 py-3 text-right">Award value (TAM)</th>
          </tr></thead>
          <tbody>
            {d.pipeline.by_status.length === 0 ? (
              <tr><td colSpan={3} className="px-6 py-6 text-slate-500 text-center">No leads yet — import some in Outreach.</td></tr>
            ) : d.pipeline.by_status.map((p) => (
              <tr key={p.status} className="border-b border-slate-100 last:border-0" data-testid={`pipeline-${p.status}`}>
                <td className="px-6 py-3 capitalize font-medium">{p.status}</td>
                <td className="px-3 py-3 text-right">{p.count}</td>
                <td className="px-6 py-3 text-right font-mono text-xs">{fmt(p.value)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
